import { useEffect, useRef } from 'react'
import { useCurrentBusiness } from '~/entities/business/model/use-business'
import {
  useEvent,
  useEventAnnouncement,
  useEventLiveRoomPolling,
} from '~/entities/event/model/use-events'
import { AnnouncementBox } from '~/features/room-announcements/components/announcement-box'
import * as m from '~/paraglide/messages'
import { MINUTES } from '~/shared/lib/time-constants'
import { useNow } from '~/shared/lib/use-now'
import type { Event } from '~/shared/types'
import { Card } from '~/shared/ui/card'

export const LIVE_ROOM_ANCHOR = 'live-room'

const ROOM_LEAD_MS = 90 * MINUTES
const ROOM_TAIL_MS = 60 * MINUTES
const CLOCK_TICK_MS = 30_000

function roomWindowOpen(event: Event, now: number): boolean {
  if (event.liveRoomMode === 'off') return false
  const start = new Date(event.startTime).getTime() - ROOM_LEAD_MS
  const end = new Date(event.endTime).getTime() + ROOM_TAIL_MS
  return start <= now && now <= end
}

export function EventAnnouncementCard({ eventId }: { eventId: string }) {
  const { data: business } = useCurrentBusiness()
  const businessId = business?.id ?? ''
  const { data: event } = useEvent(businessId, eventId)
  const now = useNow(CLOCK_TICK_MS)
  const windowOpen = !!event && roomWindowOpen(event, now)
  const { data: room } = useEventLiveRoomPolling(businessId, eventId, windowOpen)
  const announce = useEventAnnouncement(businessId, eventId)
  const cardRef = useRef<HTMLDivElement>(null)
  const shown = windowOpen && !!room && !room.closed

  useEffect(() => {
    if (shown && window.location.hash === `#${LIVE_ROOM_ANCHOR}`) {
      cardRef.current?.scrollIntoView({ block: 'start' })
    }
  }, [shown])

  if (!shown || !room) return null

  return (
    <Card
      ref={cardRef}
      id={LIVE_ROOM_ANCHOR}
      className="flex w-full scroll-mt-6 flex-col gap-3 p-4 sm:p-6"
    >
      <div className="flex items-center justify-between gap-2">
        <h3 className="font-serif font-semibold">{m.roomAnnouncementTitle()}</h3>
        <span className="shrink-0 text-muted-foreground text-xs">
          {m.liveRoomHereNow({ count: room.here })}
        </span>
      </div>
      <AnnouncementBox
        here={room.here}
        isSending={announce.isPending}
        onSend={announce.mutateAsync}
      />
    </Card>
  )
}
